import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/**
 * StickyParallaxSection Component
 * 
 * Creates a cinematic "Sticky Stack" parallax effect where:
 * 1. The section container sticks to the top of the viewport
 * 2. Content animates within the sticky container as user scrolls
 * 3. After scroll space is exhausted, the section un-sticks and next section covers it
 */
const StickyParallaxSection = () => {
  // Create a ref for the main container to track scroll position
  const ref = useRef(null);

  // Track scroll progress within this specific section
  // offset: ["start start", "end end"] means:
  // - Start tracking when the top of this section hits the top of viewport
  // - Stop tracking when the bottom of this section hits the bottom of viewport
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"]
  });

  // Transform the scroll progress (0 to 1) into various animation values
  // Scale effect: text grows from 1 to 1.5 and back to 1
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.5, 1]);
  
  // Opacity effect: fade in and out
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  
  // Y position effect: slight upward movement
  const y = useTransform(scrollYProgress, [0, 0.5, 1], [0, -50, 0]);
  
  // Rotation effect: subtle rotation for dynamic feel
  const rotate = useTransform(scrollYProgress, [0, 0.5, 1], [0, 5, 0]);

  return (
    <div 
      ref={ref}
      className="relative h-[300vh]"
      style={{ backgroundColor: '#111827' }}
    >
      {/* Sticky Container - This is what "sticks" to the viewport */}
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Content Container */}
        <motion.div 
          style={{ scale, opacity, y, rotate }}
          className="text-center max-w-4xl mx-auto px-4"
        >
          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight">
            The Future of{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Analysis
            </span>
            {' '}is Here
          </h1>
          
          {/* Sub-headline */}
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Experience the power of Algorithm-driven market analysis with real-time insights 
            that transform how you make trading decisions.
          </p>
          
          {/* Animated Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <motion.div 
              style={{ 
                scale: useTransform(scrollYProgress, [0.2, 0.4], [0.8, 1.2]),
                opacity: useTransform(scrollYProgress, [0.2, 0.4, 0.6, 0.8], [0, 1, 1, 0])
              }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2">95%</div>
              <div className="text-gray-300">Signal Accuracy</div>
            </motion.div>
            
            <motion.div 
              style={{ 
                scale: useTransform(scrollYProgress, [0.4, 0.6], [0.8, 1.2]),
                opacity: useTransform(scrollYProgress, [0.4, 0.6, 0.8, 1], [0, 1, 1, 0])
              }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-2">10K+</div>
              <div className="text-gray-300">Stocks Analyzed</div>
            </motion.div>
            
            <motion.div 
              style={{ 
                scale: useTransform(scrollYProgress, [0.6, 0.8], [0.8, 1.2]),
                opacity: useTransform(scrollYProgress, [0.6, 0.8, 1], [0, 1, 0])
              }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-indigo-400 mb-2">24/7</div>
              <div className="text-gray-300">Real-time Monitoring</div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StickyParallaxSection;
